import { create } from 'zustand'

export type AdminNotificationKind = 'message' | 'low-stock' | 'contact' | 'order'

export interface AdminNotification {
  id: string
  kind: AdminNotificationKind
  title: string
  body?: string
  link: string
  createdAt: number
  read: boolean
}

/** Id prefix of notifications that come from NotificationService (the durable, server-side staff notifications). */
export const SERVER_PREFIX = 'srv-'

/** Bounds how much history the bell keeps in memory during a single session. */
const MAX_NOTIFICATIONS = 50

interface NotificationState {
  items: AdminNotification[]
  unreadCount: number
  add: (notification: Omit<AdminNotification, 'id' | 'read' | 'createdAt'>) => void
  /** Replaces the server-backed entries (ids prefixed `srv-`) with the latest poll result; live SignalR entries are kept. */
  mergeServer: (items: AdminNotification[]) => void
  markRead: (id: string) => void
  markAllRead: () => void
  clear: () => void
}

/**
 * In-memory history for the header notification bell, fed by the SignalR bridges in
 * `src/routes/(admin)/route.tsx` (messages, low-stock, contact-form). Those bridges already
 * show a toast per event; this store additionally retains a short history so a notification
 * isn't lost the moment its toast disappears. Not persisted — a fresh session starts empty,
 * same as the toasts it complements.
 */
export const useNotificationStore = create<NotificationState>()((set) => ({
  items: [],
  unreadCount: 0,
  add: (notification) =>
    set((state) => {
      const item: AdminNotification = {
        ...notification,
        id: `${notification.kind}-${crypto.randomUUID()}`,
        createdAt: Date.now(),
        read: false,
      }
      return {
        items: [item, ...state.items].slice(0, MAX_NOTIFICATIONS),
        unreadCount: state.unreadCount + 1,
      }
    }),
  mergeServer: (incoming) =>
    set((state) => {
      const local = state.items.filter((n) => !n.id.startsWith(SERVER_PREFIX))
      const items = [...incoming, ...local].sort((a, b) => b.createdAt - a.createdAt).slice(0, MAX_NOTIFICATIONS)
      return { items, unreadCount: items.filter((n) => !n.read).length }
    }),
  markRead: (id) =>
    set((state) => {
      const target = state.items.find((n) => n.id === id)
      if (!target || target.read) return state
      return {
        items: state.items.map((n) => (n.id === id ? { ...n, read: true } : n)),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }
    }),
  markAllRead: () =>
    set((state) => ({
      items: state.items.map((n) => (n.read ? n : { ...n, read: true })),
      unreadCount: 0,
    })),
  clear: () => set({ items: [], unreadCount: 0 }),
}))
